/**
 * useDealStageSubmenus — shared hook that returns the "Move stage" and "Change
 * pipeline" ResponsiveSubmenuActions for deal records.
 *
 * Consumed by:
 *   - RecordActionMenu (full-page kebab)
 *   - SlideoutHeaderActions (deal slideout action bar)
 *
 * Centralising here keeps the two surfaces in lock-step and avoids duplicating
 * the stage-move wiring. Mirrors the rationale in useRecordActions.tsx:1-13.
 */

import { ArrowRight, SwitchHorizontal01 } from '@untitledui/icons';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useUser } from '@/allauth_auth/hooks';
import { usePermissions } from '@/hooks/usePermissions';
import type {
  ResponsiveSubmenuAction,
  ResponsiveSubmenuOption,
} from '@/lib/base/buttons';
import type { CrmObject, EntityTypeName } from '@/lib/crm/core/types';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { useDealStageMove } from '@/lib/crm/pipeline/hooks/useDealStageMove';
import { getStageEntityId } from '@/lib/crm/utils/propertyHelpers';
import { logger } from '@/lib/logger';

export const DEAL_MOVE_STAGE_KEY = 'move-stage';
export const DEAL_CHANGE_PIPELINE_KEY = 'change-pipeline';

export interface UseDealStageSubmenusParams {
  object: CrmObject;
  entityTypeName: EntityTypeName;
}

const EMPTY_SUBMENU_ACTIONS: ResponsiveSubmenuAction[] = [];

export function useDealStageSubmenus({
  object,
  entityTypeName,
}: UseDealStageSubmenusParams): ResponsiveSubmenuAction[] {
  const user = useUser();
  const isDeal = entityTypeName === 'ahoy_deal';
  // Moving a deal between stages/pipelines rewrites its stage association —
  // a write, so a seat without that grant never sees the submenus.
  const { hasPermission } = usePermissions();
  const canMoveStage = hasPermission('ahoy_deal', 'ahoy_association_added');

  const { pipelines, getDefaultPipeline } = usePipelines(
    isDeal ? object.org_id : undefined,
    'ahoy_deal'
  );

  const moveDealStage = useDealStageMove({
    orgId: object.org_id,
    userId: user?.id,
    associationTypeName: 'ahoy_deal_to_deal_pipeline_stage',
  });

  const currentStageEntityId = isDeal ? getStageEntityId(object) : null;

  const currentPipeline = useMemo(() => {
    if (!isDeal) {
      return undefined;
    }
    if (currentStageEntityId) {
      const match = pipelines.find(p =>
        p.stages.some(s => s.entity_id === currentStageEntityId)
      );
      if (match) {
        return match;
      }
    }
    return getDefaultPipeline();
  }, [isDeal, currentStageEntityId, pipelines, getDefaultPipeline]);

  const currentPipelineStages = useMemo(() => {
    if (!currentPipeline) {
      return [];
    }
    return [...currentPipeline.stages]
      .filter(s => s.entity_id != null)
      .sort((a, b) => a.properties.order - b.properties.order);
  }, [currentPipeline]);

  const isMovingRef = useRef(false);
  const [isMovingStage, setIsMovingStage] = useState(false);

  const handleMoveStage = useCallback(
    async (stageEntityId: string) => {
      if (isMovingRef.current || stageEntityId === currentStageEntityId) {
        return;
      }
      isMovingRef.current = true;
      setIsMovingStage(true);
      try {
        await moveDealStage(object, stageEntityId);
      } catch (error) {
        logger.error(
          'Failed to move deal stage',
          error instanceof Error ? error : new Error(String(error)),
          {
            component: 'crm',
            entity_id: object.entity_id,
            org_id: object.org_id,
          }
        );
        toast.error("We couldn't move the stage. Try again.");
      } finally {
        isMovingRef.current = false;
        setIsMovingStage(false);
      }
    },
    [currentStageEntityId, moveDealStage, object]
  );

  const handleChangePipeline = useCallback(
    async (pipelineKey: string) => {
      const currentPipelineKey = currentPipeline
        ? (currentPipeline.entity_id ?? currentPipeline.pipeline_name)
        : null;
      // Re-selecting the current pipeline is a no-op. Moving to its first
      // stage would silently regress a deal that's on a later stage — compare
      // pipeline keys (not the first-stage id) so only a switch to a
      // *different* pipeline moves the deal to that pipeline's first stage.
      if (isMovingRef.current || pipelineKey === currentPipelineKey) {
        return;
      }
      const target = pipelines.find(
        p => (p.entity_id ?? p.pipeline_name) === pipelineKey
      );
      if (!target) {
        return;
      }
      const firstStage = [...target.stages]
        .filter(s => s.entity_id != null)
        .sort((a, b) => a.properties.order - b.properties.order)[0];
      if (!firstStage?.entity_id) {
        return;
      }
      isMovingRef.current = true;
      setIsMovingStage(true);
      try {
        await moveDealStage(object, firstStage.entity_id);
      } catch (error) {
        logger.error(
          'Failed to change deal pipeline',
          error instanceof Error ? error : new Error(String(error)),
          {
            component: 'crm',
            entity_id: object.entity_id,
            org_id: object.org_id,
          }
        );
        toast.error("We couldn't change the pipeline. Try again.");
      } finally {
        isMovingRef.current = false;
        setIsMovingStage(false);
      }
    },
    [currentPipeline, moveDealStage, object, pipelines]
  );

  return useMemo(() => {
    if (!isDeal || user?.id === undefined || !canMoveStage) {
      return EMPTY_SUBMENU_ACTIONS;
    }

    const submenus: ResponsiveSubmenuAction[] = [];

    if (currentPipelineStages.length > 0) {
      submenus.push({
        key: DEAL_MOVE_STAGE_KEY,
        label: 'Move stage',
        icon: ArrowRight,
        options: currentPipelineStages.map(
          (s): ResponsiveSubmenuOption => ({
            key: s.entity_id as string,
            label: s.label,
            isDisabled: isMovingStage,
          })
        ),
        selectedKey: currentStageEntityId,
        onSelect: handleMoveStage,
      });
    }

    if (pipelines.length >= 2) {
      submenus.push({
        key: DEAL_CHANGE_PIPELINE_KEY,
        label: 'Change pipeline',
        icon: SwitchHorizontal01,
        options: pipelines.map(
          (p): ResponsiveSubmenuOption => ({
            key: p.entity_id ?? p.pipeline_name,
            label: p.label,
            isDisabled: isMovingStage,
          })
        ),
        selectedKey: currentPipeline
          ? (currentPipeline.entity_id ?? currentPipeline.pipeline_name)
          : null,
        onSelect: handleChangePipeline,
      });
    }

    if (submenus.length === 0) {
      return EMPTY_SUBMENU_ACTIONS;
    }

    return submenus;
  }, [
    isDeal,
    user?.id,
    canMoveStage,
    currentPipelineStages,
    currentStageEntityId,
    isMovingStage,
    handleMoveStage,
    pipelines,
    currentPipeline,
    handleChangePipeline,
  ]);
}
